import { browser } from "$app/env";
import { get, post } from "#lib/api-client.js";
import { userSettings } from "#lib/stores/userSettings.svelte.js";
import { noteDrafts } from "#lib/stores/noteDrafts.svelte.js";
import type {
	Profile,
	User,
	DropdownData,
	LoginRequest,
	Verify2FaRequest,
	ImpersonationResponse,
} from "#lib/api-client/types.gen.js";

/** Result of a login attempt. */
export type LoginResult =
	{ requires2FA: true; email: string } | { requires2FA: false; user: AuthUser };

export interface AuthUser extends User {
	team?: {
		id: string;
		name: string;
		permissions: string[];
	};
	impersonating?: string | null;
}

interface PersistedAuthState {
	user: AuthUser | null;
	dropdowns: DropdownData | null;
	darkMode: boolean | null;
}

const STORAGE_KEY = "authState";

class AuthStore {
	user: AuthUser | null = $state(null);
	dropdowns: DropdownData | null = $state(null);
	loading = $state(true);
	/** null = follow system, true = dark, false = light */
	darkMode = $state<boolean | null>(null);

	constructor() {
		if (!browser) return;
		this.#init();
	}

	#init() {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (raw) {
				const persisted = JSON.parse(raw) as PersistedAuthState;
				this.user = persisted.user ?? null;
				this.dropdowns = persisted.dropdowns ?? null;
				this.darkMode = persisted.darkMode ?? null;
			}
		} catch {
			// Corrupted cache — start fresh.
		}
		this.#applyDarkMode();
	}

	#persist() {
		if (!browser) return;
		const payload: PersistedAuthState = {
			user: this.user,
			dropdowns: this.dropdowns,
			darkMode: this.darkMode,
		};
		localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	}

	#applyDarkMode() {
		if (!browser) return;
		const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		const shouldUseDark = this.darkMode === null ? systemDark : this.darkMode;
		document.documentElement.classList.toggle("dark", shouldUseDark);
		localStorage.setItem("darkMode", JSON.stringify(this.darkMode));
	}

	toggleDarkMode() {
		// Tri-state cycle: auto → light → dark → auto.
		this.darkMode = this.darkMode === null ? false : this.darkMode ? null : true;
		this.#applyDarkMode();
		this.#persist();
	}

	async login(request: LoginRequest): Promise<LoginResult> {
		const response = await post<
			{ user: User } | { action: string; channel: string; masked_email: string }
		>("/auth/login", request);
		// 202 → the backend emailed a 2FA code; no session was created.
		if ("action" in response && response.action === "verify_2fa") {
			return { requires2FA: true, email: request.email };
		}
		this.user = { ...(response as { user: User }).user };
		this.#persist();
		// Bootstrap the csrf_ cookie with a benign authenticated GET before the
		// first state-changing request, and load the full profile.
		await this.loadUserProfile();
		await this.loadDropdowns();
		return { requires2FA: false, user: this.user };
	}

	async verify2FA(data: Verify2FaRequest): Promise<void> {
		const response = await post<{ user: User }>("/auth/2fa", data);
		this.user = { ...response.user };
		this.#persist();
		await this.loadUserProfile();
		await this.loadDropdowns();
	}

	async loadUserProfile(): Promise<void> {
		const profile = await get<Profile>("/profile");
		if (!this.user) {
			this.user = {
				id: profile.id,
				organization_id: profile.organization_id,
				name: profile.name,
				email: profile.email,
				disabled: false,
				is_admin: false,
				created_at: profile.created_at,
				updated_at: profile.updated_at,
			};
		} else {
			this.user = {
				...this.user,
				id: profile.id,
				organization_id: profile.organization_id,
				name: profile.name,
				email: profile.email,
			};
		}
		if (profile.team) {
			this.user.team = {
				id: profile.team.id ?? "",
				name: profile.team.name ?? "",
				permissions: profile.team.permissions ?? [],
			};
		}
		this.user.impersonating = profile.impersonating ?? null;
		this.loading = false;
		this.#persist();
	}

	async loadDropdowns(): Promise<void> {
		this.dropdowns = await get<DropdownData>("/dropdowns");
		this.#persist();
	}

	async impersonate(userId: string): Promise<void> {
		await post<ImpersonationResponse>(`/impersonate/${userId}`);
		await this.loadUserProfile();
		await this.loadDropdowns();
	}

	async endImpersonation(): Promise<void> {
		await post<{ message: string }>("/endimpersonate");
		await this.loadUserProfile();
		await this.loadDropdowns();
	}

	async refreshCache(): Promise<void> {
		await this.loadUserProfile();
		await this.loadDropdowns();
	}

	async logout(): Promise<void> {
		try {
			await post("/auth/logout");
		} catch {
			// Logging out locally even if the server call fails.
		}
		// Note drafts are plaintext client-side data; drop them when the user
		// explicitly signs out of a possibly shared machine.
		if (this.user) noteDrafts.clearUser(this.user.id);
		this.user = null;
		this.dropdowns = null;
		this.loading = false;
		userSettings.clear();
		if (browser) {
			localStorage.removeItem(STORAGE_KEY);
		}
	}

	/** Re-check the session on app boot; clears stale cached state on failure. */
	async checkAuth(): Promise<void> {
		if (!browser) return;
		try {
			await this.loadUserProfile();
			// Dropdowns are loaded on login; a restored session (page reload, SSO)
			// must load them too, otherwise every select in the app is empty.
			await this.loadDropdowns();
		} catch {
			this.user = null;
			this.dropdowns = null;
			this.loading = false;
			// Keep note drafts on an expired session: the user did not sign out
			// and can resume the note after signing back in.
			if (browser) {
				localStorage.removeItem(STORAGE_KEY);
			}
		}
	}
}

export const auth = new AuthStore();
